// Shared cadence for screens that wait on asynchronous server work without the
// customer doing anything: payment confirmation (Success) and the Doodles
// multicolor price that is calculated from the figurine's own slice
// (ADR-DSP1, 409 `doodles_price_pending`). Reads are bounded; a manual
// "try again" starts a fresh run.

/** Quick reads first (about 31 seconds in total), matching payment confirmation. */
export const CONFIRMATION_POLL_DELAYS_MS = [0, 1000, 2000, 3000, 5000, 5000, 5000, 5000, 5000] as const;
/** Slower background reads after the quick run. */
export const BACKGROUND_POLL_DELAY_MS = 15_000;
/** Show a reassuring "still working" line after this long. */
export const PENDING_STILL_WORKING_AFTER_MS = 45_000;
/** Stop reading and offer an explicit retry after this long. */
export const PENDING_BACKSTOP_MS = 120_000;

/**
 * The confirmation cadence, continued at the background interval, cut off once
 * the cumulative wait would pass `backstopMs`.
 */
export function boundedPendingDelays(backstopMs: number = PENDING_BACKSTOP_MS): number[] {
  const delays: number[] = [];
  let elapsed = 0;
  for (const delay of CONFIRMATION_POLL_DELAYS_MS) {
    if (elapsed + delay > backstopMs) return delays;
    delays.push(delay);
    elapsed += delay;
  }
  while (elapsed + BACKGROUND_POLL_DELAY_MS <= backstopMs) {
    delays.push(BACKGROUND_POLL_DELAY_MS);
    elapsed += BACKGROUND_POLL_DELAY_MS;
  }
  return delays;
}

export type PendingRetryResult<T> = { kind: "ready"; value: T } | { kind: "exhausted" } | { kind: "cancelled" };

/**
 * Calls `load` on the bounded cadence while it rejects with a pending error.
 * Any other rejection is rethrown to the caller unchanged.
 */
export async function retryWhilePending<T>(
  load: () => Promise<T>,
  options: {
    isPending: (error: unknown) => boolean;
    onPending?: (error: unknown) => void;
    isCancelled: () => boolean;
    wait: (delayMs: number) => Promise<void>;
    delays?: readonly number[];
  }
): Promise<PendingRetryResult<T>> {
  for (const delayMs of options.delays ?? boundedPendingDelays()) {
    if (delayMs > 0) await options.wait(delayMs);
    if (options.isCancelled()) return { kind: "cancelled" };
    try {
      const value = await load();
      if (options.isCancelled()) return { kind: "cancelled" };
      return { kind: "ready", value };
    } catch (error: unknown) {
      if (options.isCancelled()) return { kind: "cancelled" };
      if (!options.isPending(error)) throw error;
      options.onPending?.(error);
    }
  }
  return { kind: "exhausted" };
}
